// src/components/organizer/EventCalendarWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Place as LocationIcon,
  People as PeopleIcon,
  ArrowForward as NextIcon,
  CalendarToday as CalendarIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';

const EventCalendarWidget = ({ data }) => {
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  // Eventos del calendario (simulados)
  const upcomingEvents = [
    {
      id: 1,
      name: 'Maratón Ciudad 2024',
      date: '2024-12-20',
      time: '07:00',
      location: 'Parque Central',
      participants: 450,
      status: 'confirmed',
      type: 'running',
      daysUntil: 11
    },
    {
      id: 2,
      name: 'Copa Juvenil Natación',
      date: '2024-12-25',
      time: '09:00',
      location: 'Centro Acuático',
      participants: 120,
      status: 'planning',
      type: 'swimming',
      daysUntil: 16
    },
    {
      id: 3,
      name: 'Triatlón Regional',
      date: '2025-01-15',
      time: '06:30',
      location: 'Costa del Sol',
      participants: 85,
      status: 'registration',
      type: 'triathlon',
      daysUntil: 37
    }
  ];

  const todayEvents = upcomingEvents.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.toDateString() === currentDate.toDateString();
  });

  const thisWeekEvents = upcomingEvents.filter(event => {
    const eventDate = new Date(event.date);
    const weekFromNow = new Date();
    weekFromNow.setDate(currentDate.getDate() + 7);
    return eventDate >= currentDate && eventDate <= weekFromNow;
  });

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'success',
      planning: 'warning',
      registration: 'info',
      completed: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: 'Confirmado',
      planning: 'Planificando',
      registration: 'Inscripciones',
      completed: 'Completado'
    };
    return labels[status] || status;
  };

  const getTypeEmoji = (type) => {
    const emojis = {
      running: '🏃',
      swimming: '🏊',
      triathlon: '🏊‍♂️🚴‍♂️🏃‍♂️',
      cycling: '🚴',
      football: '⚽'
    };
    return emojis[type] || '🏃';
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil <= 7) return 'error';
    if (daysUntil <= 14) return 'warning';
    return 'info';
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📅 Mi Calendario"
        subheader={currentMonth}
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Ver calendario completo">
              <IconButton size="small">
                <CalendarIcon />
              </IconButton>
            </Tooltip>
            <Chip 
              label={`${upcomingEvents.length} eventos`} 
              color="primary" 
              size="small"
            />
          </Box>
        }
      />
      
      <CardContent>
        {/* Eventos de hoy */}
        {todayEvents.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom color="primary.main">
              🎯 Hoy
            </Typography>
            {todayEvents.map((event) => (
              <Paper key={event.id} sx={{ p: 2, mb: 1, bgcolor: 'primary.light' }}>
                <Typography variant="body2" fontWeight="bold" color="primary.dark">
                  {getTypeEmoji(event.type)} {event.name}
                </Typography>
                <Typography variant="caption" color="primary.dark">
                  {event.time} • {event.location}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Próximos eventos */}
        <Typography variant="subtitle2" gutterBottom>
          📋 Próximos Eventos
        </Typography>
        
        <List dense>
          {upcomingEvents.slice(0, 4).map((event) => (
            <ListItem key={event.id} divider>
              <Box sx={{ mr: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: `${getStatusColor(event.status)}.main`,
                    width: 32,
                    height: 32
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {event.daysUntil}
                  </Typography>
                </Avatar>
              </Box>
              
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="bold">
                      {getTypeEmoji(event.type)} {event.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(event.status)}
                      color={getStatusColor(event.status)}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 16 }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      📅 {new Date(event.date).toLocaleDateString('es-ES', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })} • {event.time}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      📍 {event.location} • 👥 {event.participants} participantes
                    </Typography>
                  </Box>
                }
              />
              
              <Box>
                <Chip
                  label={`${event.daysUntil}d`}
                  color={getUrgencyColor(event.daysUntil)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Estadísticas del mes */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box textAlign="center" sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="h6" color="primary.main">
                {upcomingEvents.length}
              </Typography>
              <Typography variant="caption">Eventos Programados</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center" sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="h6" color="success.main">
                {upcomingEvents.reduce((sum, e) => sum + e.participants, 0)}
              </Typography>
              <Typography variant="caption">Total Participantes</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Alertas */}
        {upcomingEvents.filter(e => e.daysUntil <= 7).length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="caption" color="warning.dark">
              ⚠️ {upcomingEvents.filter(e => e.daysUntil <= 7).length} evento(s) en los próximos 7 días
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EventCalendarWidget;